'use client';

import * as React from 'react';
import { colors } from '@federa/components/css-refs.colors';
import { spacing } from '@federa/components/css-refs.spacing';
import { typography } from '@federa/components/css-refs.typography';
import { borderRadius, motion } from '@federa/components/css-refs.effects';
import { FLEX_ROW_CENTER_STYLE } from '@federa/const/style-flex-presets';
import { ARIA_LABEL_NEXT_IMAGE, ARIA_LABEL_PREVIOUS_IMAGE } from '@federa/const/aria-label-values';
import { PROPERTY_IMAGE_DELIVERY_WIDTH } from '@federa/const/urls-connect';
import { withDeliveryWidth } from '@federa/utils/images/delivery-variant';
import { CarouselNavButton } from './CarouselNavButton.tsx';
import { IMAGE_CONTAINER_BASE_STYLES, IMAGE_STYLES, NO_IMAGE_STYLES } from './property-card-styles.ts';

const carouselTokens = {
	maxDots: 5,
	containerPaddingVertical: spacing[1],
	containerPaddingHorizontal: spacing[2],
	containerBackground: colors.overlay.medium,
	gap: spacing[1],
	dotSize: spacing[1.5],
	activeDotSize: spacing[2],
	dotColor: colors.overlay.mediumTranslucent,
	activeDotColor: colors.overlay.lightest,
} as const;

const DOTS_CONTAINER_STYLES: React.CSSProperties = {
	position: 'absolute',
	left: '50%',
	bottom: spacing[3],
	transform: 'translateX(-50%)',
	...FLEX_ROW_CENTER_STYLE,
	gap: carouselTokens.gap,
	padding: `${carouselTokens.containerPaddingVertical} ${carouselTokens.containerPaddingHorizontal}`,
	backgroundColor: carouselTokens.containerBackground,
	borderRadius: borderRadius.full,
};

function useVisibleDotIndices(totalImages: number, currentImageIndex: number): number[] {
	return React.useMemo(() => {
		if (totalImages <= 1) return [];
		const maxDots = carouselTokens.maxDots;
		const visibleCount = Math.min(totalImages, maxDots);
		let start = 0;
		if (totalImages > maxDots) {
			const midpoint = Math.floor(maxDots / 2);
			if (currentImageIndex <= midpoint) start = 0;
			else if (currentImageIndex >= totalImages - midpoint - 1) start = totalImages - maxDots;
			else start = currentImageIndex - midpoint;
		}
		return Array.from({ length: visibleCount }, (_, i) => start + i);
	}, [totalImages, currentImageIndex]);
}

function getDotStyles(isActive: boolean): React.CSSProperties {
	const size = isActive ? carouselTokens.activeDotSize : carouselTokens.dotSize;
	return {
		display: 'inline-block',
		width: size,
		height: size,
		borderRadius: borderRadius.full,
		backgroundColor: isActive ? carouselTokens.activeDotColor : carouselTokens.dotColor,
		transition: `all ${motion.duration.fast} ${motion.easing.standard}`,
	};
}

function CarouselDots({ visibleDotIndices, currentImageIndex }: { visibleDotIndices: number[]; currentImageIndex: number }) {
	if (visibleDotIndices.length === 0) return null;
	return (
		<div style={DOTS_CONTAINER_STYLES}>
			{visibleDotIndices.map((dotIndex) => (
				<span key={dotIndex} style={getDotStyles(dotIndex === currentImageIndex)} />
			))}
		</div>
	);
}

export function PropertyCardImage({
	images,
	sentinelImageSrc,
	eagerImage,
	currentImageIndex,
	onPrevious,
	onNext,
}: {
	images: string[];
	/** Brand-owned no-image placeholder (threaded from the host app; DS-BRAND-IMPORT-001). */
	sentinelImageSrc?: string;
	/** Above-the-fold card: fetch the cover now, at high priority, instead of when it nears the viewport. */
	eagerImage: boolean;
	currentImageIndex: number;
	onPrevious: (e: React.MouseEvent) => void;
	onNext: (e: React.MouseEvent) => void;
}) {
	const totalImages = images.length;
	const visibleDotIndices = useVisibleDotIndices(totalImages, currentImageIndex);
	// Delivered at card width: the stored URL is the 4096px original (`withDeliveryWidth` states the cost).
	const imageUrl = withDeliveryWidth((totalImages > 0 && images[currentImageIndex] ? images[currentImageIndex] : sentinelImageSrc) ?? '', PROPERTY_IMAGE_DELIVERY_WIDTH.CARD);
	return (
		<div className="group" style={IMAGE_CONTAINER_BASE_STYLES}>
			{/* Decorative: the card's own text names the property, and an empty url renders NO element rather than an
			    `<img src="">`, which the browser resolves to the page itself and fetches. */}
			{imageUrl !== '' && (
				<img
					src={imageUrl}
					alt=""
					loading={eagerImage ? 'eager' : 'lazy'}
					fetchPriority={eagerImage ? 'high' : undefined}
					decoding="async"
					draggable={false}
					style={IMAGE_STYLES}
				/>
			)}
			{totalImages === 0 && (
				<div style={NO_IMAGE_STYLES}>
					<span
						style={{
							color: colors.text.disabled,
							fontSize: typography.fontSize.xs,
						}}
					>
						No image available
					</span>
				</div>
			)}
			{totalImages > 1 && (
				<>
					<CarouselNavButton onClick={onPrevious} side="left" ariaLabel={ARIA_LABEL_PREVIOUS_IMAGE} iconName="previous" revealOnHover />
					<CarouselNavButton onClick={onNext} side="right" ariaLabel={ARIA_LABEL_NEXT_IMAGE} iconName="next" revealOnHover />
					<CarouselDots visibleDotIndices={visibleDotIndices} currentImageIndex={currentImageIndex} />
				</>
			)}
		</div>
	);
}
